import { ChangeDetectionStrategy, Component, inject, signal, ViewEncapsulation } from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { ITodoListItem } from '@fi-app/fi-onboarding/fi-todo/fi-todo';
import { MatSortDirection, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IItemsListParameterBase,
    ISideListOptionsItem,
} from '@fi-shared/fi-items-list/fi-items-list';
import { FiTodoService } from '@fi-app/fi-onboarding/fi-todo/fi-todo.service';
import { FiTodoActionsBuilderService } from '@fi-app/fi-onboarding/fi-todo/fi-todo-actions-builder.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';

@Component({
    selector: 'fi-todo-list',
    templateUrl: './fi-todo-list.component.html',
    styleUrl: './fi-todo-list.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-todo-list' },
    standalone: false,
})
export class FiTodoListComponent extends FiRemoteStateFullDataSource<ITodoListItem> {
    public readonly defaultSortColumnName = 'CreatedOn';
    public readonly defaultSortDirection = true;
    public readonly objectType = ObjectTypes.TodoList;
    public readonly itemsListType = FiItemsListType.TodoListItems;

    private fiTodoActionsBuilderService = inject(FiTodoActionsBuilderService);
    private fiTodoService = inject(FiTodoService);
    public getRowClassFn = (todoListItem: ITodoListItem): string =>
        todoListItem.HasEditAccess ? 'cursor-pointer' : '';

    protected readonly sideListOptions: ISideListOptionsItem[] = [
        { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
    ];
    protected readonly actions: IItemsListAction2<ITodoListItem>[] = this.fiTodoActionsBuilderService.getTodoActions({
        onTodoDeletedFn: (item) => this.removeData([item]),
    });

    protected isEmptyListIsShown = signal(false);

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<ITodoListItem>> {
        return new Promise<IItemsListDataFetchResult<ITodoListItem>>((resolve, reject) => {
            const parameter = this.getParameter(loadArgs);
            this.fiTodoService.getTodosListData(parameter).then((data) => {
                if (this.loadingCounter() === 1) {
                    this.isEmptyListIsShown.set(data.Items.length === 0);
                }
                resolve(data);
            }, reject);
        });
    }

    private getParameter(loadArgs: ILoadDataArgs): IItemsListParameterBase {
        const parameter = super.getItemsListParameter(loadArgs);
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        return parameter;
    }

    protected getItemSharedStatusLabel(isShared: boolean): string {
        return this.appTextService.get(isShared ? 'common.public' : 'task.private');
    }

    protected navigateToTodoView(todoListItem: ITodoListItem): void {
        const viewState = FiStateNames.Onboarding.Todo.View;
        this.fiStateService.registerReturnToCurrentState(viewState);
        this.fiStateService.go(viewState, { id: todoListItem.Id });
    }

    protected readonly FiStateNames = FiStateNames;
    protected readonly MatSortDirection = MatSortDirection;
}
